import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Shield, ArrowLeft, Clock, Calendar, AlertCircle,
  CheckCircle, BookOpen, ChevronRight, Timer
} from 'lucide-react';
import axios from 'axios';
import AppFooter from '../components/AppFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AccountTimelinePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTimeline();
  }, []);

  const loadTimeline = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/account-timeline`, { withCredentials: true });
      setTimelineData(data);
    } catch (e) {
      console.error('Failed to load timeline:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const getStatusColor = (item) => {
    if (item.is_expired) return { bg: 'bg-[#4CAF50]/10', border: 'border-[#4CAF50]/30', text: 'text-[#4CAF50]' };
    if (item.approaching_expiry) return { bg: 'bg-[#E0BFB8]/20', border: 'border-[#B76E79]/30', text: 'text-[#B76E79]' };
    return { bg: 'bg-[#F5F5F5]', border: 'border-[#36454F]/10', text: 'text-[#36454F]' };
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="account-timeline-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">Selah Slate&trade;</span>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-[#36454F]/70 hover:text-[#36454F]">Sign Out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />Back to Dashboard
        </Link>

        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#36454F]">
            Account Lifecycle Timeline
          </h1>
          <p className="mt-2 text-[#36454F]/65 font-['Manrope']">
            Track when negative items will age off your credit report (7-year rule)
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#36454F]/65 font-['Manrope']">Loading timeline...</p>
          </div>
        ) : !timelineData?.items?.length ? (
          <Card className="border-[#36454F]/15 bg-white animate-fade-in-up">
            <CardContent className="p-12 text-center">
              <Calendar className="w-16 h-16 text-[#36454F]/20 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-2">
                No Timeline Data Yet
              </h3>
              <p className="text-[#36454F]/65 font-['Manrope'] mb-6 max-w-md mx-auto">
                Upload and analyze a credit report to see when negative items will fall off your record.
              </p>
              <Link to="/upload">
                <Button className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="upload-report-btn">
                  Upload Credit Report <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Faith Guidance */}
            {timelineData.faith_guidance && (
              <Card className="border-[#E0BFB8] bg-[#E0BFB8]/10 animate-fade-in-up" data-testid="faith-guidance-card">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <BookOpen className="w-6 h-6 text-[#B76E79] flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <div>
                      <p className="font-['Playfair_Display'] text-lg italic text-[#36454F] mb-1">
                        "{timelineData.faith_guidance.verse}"
                      </p>
                      <p className="text-sm text-[#36454F]/60 font-['Manrope'] mb-2">— {timelineData.faith_guidance.reference}</p>
                      <p className="text-sm text-[#36454F]/80 font-['Manrope']">
                        {timelineData.faith_guidance.practical_steps?.[0]}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-fade-in-up animation-delay-100">
              <Card className="border-[#36454F]/15 bg-white">
                <CardContent className="p-4 text-center">
                  <span className="text-2xl font-['Manrope'] font-semibold text-[#36454F]">
                    {timelineData.items.length}
                  </span>
                  <p className="text-xs text-[#36454F]/50 font-['Manrope'] mt-1">Total Items</p>
                </CardContent>
              </Card>
              <Card className="border-[#B76E79]/30 bg-[#E0BFB8]/10">
                <CardContent className="p-4 text-center">
                  <span className="text-2xl font-['Manrope'] font-semibold text-[#B76E79]">
                    {timelineData.items.filter(i => i.approaching_expiry).length}
                  </span>
                  <p className="text-xs text-[#36454F]/50 font-['Manrope'] mt-1">Expiring Soon</p>
                </CardContent>
              </Card>
              <Card className="border-[#4CAF50]/30 bg-[#4CAF50]/5">
                <CardContent className="p-4 text-center">
                  <span className="text-2xl font-['Manrope'] font-semibold text-[#4CAF50]">
                    {timelineData.items.filter(i => i.is_expired).length}
                  </span>
                  <p className="text-xs text-[#36454F]/50 font-['Manrope'] mt-1">Ready to Remove</p>
                </CardContent>
              </Card>
            </div>

            {/* Timeline Items */}
            <div className="space-y-3 animate-fade-in-up animation-delay-200">
              {timelineData.items.map((item, index) => {
                const colors = getStatusColor(item);
                return (
                  <Card key={index} className={`border ${colors.border} bg-white`} data-testid={`timeline-item-${index}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-['Manrope'] font-semibold text-[#36454F]">
                              {item.account_name}
                            </h3>
                            {item.is_expired ? (
                              <span className="text-xs px-2 py-0.5 bg-[#4CAF50]/10 text-[#4CAF50] rounded font-['Manrope']">
                                Ready to Remove
                              </span>
                            ) : item.approaching_expiry ? (
                              <span className="text-xs px-2 py-0.5 bg-[#E0BFB8]/30 text-[#B76E79] rounded font-['Manrope']">
                                Expiring Soon
                              </span>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#36454F]/50 font-['Manrope'] mb-3">
                            <span>{item.account_number}</span>
                            <span>{item.bureau}</span>
                            <span>{item.issue_type}</span>
                          </div>

                          {/* Progress Bar */}
                          <div className="mb-2">
                            <div className="flex items-center justify-between text-xs font-['Manrope'] mb-1">
                              <span className="text-[#36454F]/50">Reported: {item.date_reported}</span>
                              <span className={colors.text}>Falls off: {item.falloff_date}</span>
                            </div>
                            <Progress value={item.percentage_complete} className="h-2 bg-[#36454F]/10" />
                          </div>

                          <div className="flex items-center space-x-1 text-xs font-['Manrope']">
                            <Timer className="w-3 h-3 text-[#36454F]/40" strokeWidth={1.5} />
                            <span className={colors.text}>
                              {item.is_expired
                                ? 'Eligible for removal — dispute now!'
                                : `${item.years_remaining} years remaining (${item.days_remaining} days)`}
                            </span>
                          </div>
                        </div>

                        {/* Circular Progress */}
                        <div className="ml-4 flex-shrink-0">
                          <div className="relative w-14 h-14">
                            <svg className="w-14 h-14 transform -rotate-90">
                              <circle cx="28" cy="28" r="24" fill="none" stroke="rgba(54, 69, 79, 0.1)" strokeWidth="4" />
                              <circle cx="28" cy="28" r="24" fill="none" stroke={item.is_expired ? '#4CAF50' : item.approaching_expiry ? '#B76E79' : '#36454F'} strokeWidth="4" strokeDasharray={`${(item.percentage_complete / 100) * 151} 151`} />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="font-['Manrope'] font-semibold text-xs text-[#36454F]">
                                {Math.round(item.percentage_complete)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action for expired items */}
                      {item.is_expired && (
                        <div className="mt-3 pt-3 border-t border-[#36454F]/10">
                          <Link to="/upload">
                            <Button size="sm" className="bg-[#4CAF50] hover:bg-[#4CAF50]/90 text-white font-['Manrope'] rounded-sm" data-testid={`dispute-expired-${index}`}>
                              Dispute for Removal <ChevronRight className="w-3 h-3 ml-1" strokeWidth={1.5} />
                            </Button>
                          </Link>
                        </div>
                      )}

                      {/* Tip for approaching expiry */}
                      {item.approaching_expiry && !item.is_expired && (
                        <div className="mt-3 pt-3 border-t border-[#36454F]/10">
                          <div className="flex items-start space-x-2 p-2 bg-[#E0BFB8]/10 rounded-sm">
                            <AlertCircle className="w-4 h-4 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                            <p className="text-xs text-[#36454F]/70 font-['Manrope']">
                              <strong>Do NOT</strong> make payments on this account — it could restart the 7-year clock. Let it age off naturally.
                            </p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Education Box */}
            <Card className="border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-300">
              <CardContent className="p-6">
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mb-4">
                  Understanding the 7-Year Rule
                </h3>
                <div className="space-y-3 text-sm text-[#36454F]/80 font-['Manrope']">
                  <p>Under the FCRA, most negative items must be removed from your credit report <strong>7 years</strong> after the Date of First Delinquency (DOFD).</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 bg-[#F5F5F5] rounded-sm">
                      <p className="font-semibold text-[#36454F] mb-1">Do:</p>
                      <ul className="text-xs space-y-1 text-[#36454F]/70">
                        <li>Track your DOFD carefully</li>
                        <li>Dispute items past the 7-year mark</li>
                        <li>Build positive credit in the meantime</li>
                      </ul>
                    </div>
                    <div className="p-3 bg-[#F5F5F5] rounded-sm">
                      <p className="font-semibold text-[#D9534F] mb-1">Don't:</p>
                      <ul className="text-xs space-y-1 text-[#36454F]/70">
                        <li>Make payments on statute-barred debts</li>
                        <li>Acknowledge the debt in writing</li>
                        <li>Let collectors pressure you into restarting the clock</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Re-Aged Accounts */}
            <Card className="border-[#D9534F]/20 bg-white animate-fade-in-up animation-delay-300">
              <CardContent className="p-6">
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] mb-2 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-[#D9534F]" strokeWidth={1.5} />
                  Watch Out: Re-Aged Accounts
                </h3>
                <p className="text-sm text-[#36454F]/80 font-['Manrope'] mb-4">
                  <strong>Re-aging</strong> is when a collector or bureau illegally resets the reporting date on an old debt to make it appear newer. This is a violation of FCRA Section 605(c).
                </p>
                <div className="space-y-2 text-sm font-['Manrope']">
                  <div className="p-3 bg-[#D9534F]/5 border border-[#D9534F]/10 rounded-sm">
                    <p className="font-semibold text-[#D9534F] text-xs mb-1">How to spot it:</p>
                    <p className="text-xs text-[#36454F]/70">If a collection account shows a date newer than when you originally missed payment on the original creditor, it may have been re-aged.</p>
                  </div>
                  <div className="p-3 bg-[#F5F5F5] rounded-sm">
                    <p className="font-semibold text-[#36454F] text-xs mb-1">What to do:</p>
                    <ul className="text-xs space-y-1 text-[#36454F]/70">
                      <li>Compare the DOFD with original creditor records</li>
                      <li>Dispute citing: "Date of First Delinquency altered in violation of 15 U.S.C. § 1681c(c)"</li>
                      <li>Request the original DOFD from the original creditor in writing</li>
                      <li>Do NOT make any payment — it could justify the new date</li>
                      <li>File a CFPB complaint if the bureau won't correct it</li>
                    </ul>
                  </div>
                  <div className="p-3 bg-[#E0BFB8]/10 border-l-3 border-l-[#B76E79] rounded-r-sm">
                    <p className="text-xs text-[#36454F]/60 font-['Playfair_Display'] italic">
                      "No weapon formed against you shall prosper." — Isaiah 54:17
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      <AppFooter />
    </div>
  );
}
